import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { NeonBackground } from "../components/NeonBackground";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("La password deve avere almeno 6 caratteri."); return; }
    if (password !== confirm) { setError("Le password non coincidono."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Impossibile reimpostare la password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="noise-overlay min-h-screen flex items-center justify-center px-5">
      <NeonBackground />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-black/60 backdrop-blur-xl border border-white/10 rounded-sm p-8 sm:p-10 animate-glow-breathe">
        <div className="flex justify-center mb-6">
          <img src="/logo-web.png" alt="AC Creator 3D" className="h-20 w-auto animate-logo" />
        </div>
        <h1 className="font-display font-bold text-2xl tracking-tight text-white">Nuova password</h1>

        {!token ? (
          <p data-testid="reset-notoken" className="text-sm text-neon-pink font-mono mt-4">Link non valido: token mancante.</p>
        ) : done ? (
          <div data-testid="reset-done" className="flex items-start gap-3 p-4 mt-4 rounded-sm border border-neon-green/40 bg-neon-green/5 text-sm text-white font-light">
            <CheckCircle2 className="w-5 h-5 text-neon-green shrink-0 mt-0.5" /> <span>Password aggiornata! Ti sto reindirizzando all'accesso…</span>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground font-light mt-2 mb-8">Scegli una nuova password per il tuo account.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Nuova password</Label>
                <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="fld" />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Conferma password</Label>
                <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="fld" />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-neon-pink font-mono">{error}</p>}
              <Button data-testid="reset-submit-btn" type="submit" disabled={loading}
                className="w-full h-12 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2 transition-colors duration-200 disabled:opacity-60">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvataggio…</> : "Reimposta password"}
              </Button>
            </form>
          </>
        )}

        <Link to="/login" data-testid="reset-back-login"
          className="mt-6 flex items-center justify-center gap-2 text-sm font-mono text-muted-foreground hover:text-neon-cyan transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> Torna all'accesso
        </Link>
      </motion.div>
      <style>{`.fld{background:hsl(var(--input));border-color:hsl(var(--border));color:white}.fld:focus-visible{outline:none;box-shadow:0 0 0 2px #00F0FF}`}</style>
    </div>
  );
}
